import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

// Get browser timezone (e.g., "Asia/Tokyo", "America/New_York")
const getTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// Generate X-Request-Id
// Format: aw + 14-digit timestamp (20250302120000) + 6-char random string
const generateRequestId = (): string => {
  const prefix = 'aw';
  const timestamp = new Date()
    .toISOString()
    .replace(/[-:T.Z]/g, '')
    .slice(0, 14);
  const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${prefix}${timestamp}${randomStr}`;
};

// Create axios instance
const request = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
request.interceptors.request.use(
  config => {
    // Add X-Request-Id
    config.headers['X-Request-Id'] = generateRequestId();

    // Add X-Time-Zone header for server-side time handling
    config.headers['X-Time-Zone'] = getTimeZone();

    // FormData 上传（如媒体文件）：删除 Content-Type，由浏览器自动设置 multipart boundary。
    // 不能保留默认的 application/json，否则请求无 boundary、后端 MultipartReader 解析失败
    if (config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    } else if (!config.headers.has('Content-Type')) {
      // 仅当调用方未显式指定 Content-Type 时才兜底为 JSON。
      // 用 AxiosHeaders 的 has/set（大小写不敏感）而非原始属性访问：
      // axios 内部把 'content-type' 小写化存储，直接 config.headers['Content-Type']
      // 访问不到小写键，判断会失准并可能覆盖单次请求手动设置的 Content-Type。
      config.headers.set('Content-Type', 'application/json');
    }

    // Get token from authStore and add Authorization header
    const { token } = useAuthStore.getState();
    if (token) {
      config.headers['Authorization'] = token;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Flag to prevent duplicate logout calls on 401
let isHandling401 = false;

// Handle 401 unified logic
const handle401 = async () => {
  if (isHandling401) return;
  isHandling401 = true;

  const { logout } = useAuthStore.getState();
  await logout();
  window.location.href = '/login';
};

// Response interceptor
request.interceptors.response.use(
  async response => {
    // For blob responses, check if it's actually an error (JSON response)
    if (response.config.responseType === 'blob') {
      const contentTypeHeader = response.headers['content-type'];
      const contentType = Array.isArray(contentTypeHeader)
        ? contentTypeHeader.join(';')
        : typeof contentTypeHeader === 'string'
          ? contentTypeHeader
          : '';
      // If content-type is JSON, it's an error response
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        if (errorData.code !== 0) {
          return Promise.reject(new Error(errorData.msg || 'Request failed'));
        }
        return errorData;
      }
      return response;
    }

    const { code, msg } = response.data;

    // Business-layer 401: token expired, clear login state and redirect to login
    // Exclude login requests to avoid redirect on login failure
    if (code === 401) {
      const config = response.config;
      if (!config.url?.includes('/login')) {
        await handle401();
      }
      return Promise.reject(new Error(msg || 'Session expired, please log in again'));
    }

    // Business error handling
    if (code !== 0) {
      console.error('Request error:', msg);
      return Promise.reject(new Error(msg || 'Request failed'));
    }

    return response.data;
  },
  async error => {
    // HTTP error handling
    if (error.response) {
      const { status, config } = error.response;

      // HTTP 401: clear login state and redirect to login
      // Exclude login and logout requests to avoid infinite loops
      if (status === 401 && !config.url?.includes('/login') && !config.url?.includes('/logout')) {
        await handle401();
      }

      console.error('HTTP error:', status, error.response.data);
    } else {
      console.error('Network error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default request;
